import React, { useState, useContext, ChangeEvent } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig, RoleUtils } from '@galaxy/lorule-react-commons';
import { Modal, Button, Alert } from 'react-bootstrap';
import { ResponseError, SubmitButton, DateField } from '@nintendo/forms';
import moment from 'moment-timezone';
import { useMsal } from '@azure/msal-react';
import ConfigContext from '../../../contexts/ConfigContext';
import {
    INITIAL_SELECT_ID,
    DATE_ONLY_FORMAT,
    SERVER_TIMEZONE,
    COMMENT_MAX_LENGTH,
    MAXIMUM_COMPLETION_DAYS,
    MINIMUM_COMPLETION_DAYS,
} from '../../../utils/Constants';
import NinModal from '../../common/modal/NinModal';
import {
    cancelActionMemo,
    reactivateActionMemo,
    reopenActionMemo,
    closeActionMemo,
    deactivateActionMemo,
} from '../../../api/actionMemoStatusApi';
import { sendToast, formatAmDisplayString } from '../../../utils/displayUtils';
import TextCountedField from '../../common/TextCountedField';
import { changeCompletionDate, markConfidential } from '../../../api/actionMemoApi';
import { cancelRequest } from '../../../api/statusChangeRequestApi';
import ModalConfirmCheckbox from '../../common/modal/ModalConfirmCheckBox';

export enum AmProcessFormType {
    CANCEL = 'CANCEL',
    CANCEL_REQUEST = 'CANCEL_REQUEST',
    CHANGE_COMPLETION_DATE = 'CHANGE_COMPLETION_DATE',
    MARK_CONFIDENTIAL = 'MARK_CONFIDENTIAL',
    UNMARK_CONFIDENTIAL = 'UNMARK_CONFIDENTIAL',
    CLOSE = 'CLOSE',
    REOPEN = 'REOPEN',
    DEACTIVATE = 'DEACTIVATE',
    REACTIVATE = 'REACTIVATE',
    HOLD = 'HOLD',
    RELEASE = 'RELEASE',
}

export interface AmProcessFormBaseProps {
    title: String;
    toggleButton: JSX.Element;
    formType: AmProcessFormType;
    onSubmit?: () => void;
}

export interface AmProcessModalProps extends AmProcessFormBaseProps {
    amId: number;
    amNumber: number;
    supNumber: number;
    creationDate?: string;
    completionDate?: string | null;
    newCompletionDate?: string | null;
    templateMaximumCompletionDays?: number;
}

const submitAmProcess = async (
    config: LoruleRequestConfig,
    formType: AmProcessFormType,
    values: {
        amId: number;
        amNumber: number;
        supNumber: number;
        completionDate: string;
        commentMessage: string;
    },
    roles: string[],
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        if (formType === AmProcessFormType.CANCEL) {
            await cancelActionMemo(
                config,
                values.amId,
                values.commentMessage,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            );
            sendToast(
                <>
                    Cancelled Action Memo:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmProcessFormType.CHANGE_COMPLETION_DATE) {
            await changeCompletionDate(
                config,
                values.amId,
                new Date(values.completionDate),
                values.commentMessage,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            );
            sendToast(
                <>
                    Changed Completion Date:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmProcessFormType.MARK_CONFIDENTIAL) {
            await markConfidential(
                config,
                values.amId,
                true,
                values.commentMessage,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            );
            sendToast(
                <>
                    Marked Confidential:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmProcessFormType.UNMARK_CONFIDENTIAL) {
            await markConfidential(
                config,
                values.amId,
                false,
                values.commentMessage,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            );
            sendToast(
                <>
                    Unmarked Confidential:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmProcessFormType.CLOSE) {
            await closeActionMemo(config, values.amId, values.commentMessage);
            sendToast(
                <>
                    Closed Action Memo:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmProcessFormType.REOPEN) {
            await reopenActionMemo(
                config,
                values.amId,
                new Date(values.completionDate),
                values.commentMessage,
            );
            sendToast(
                <>
                    Reopened Action Memo:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmProcessFormType.DEACTIVATE) {
            await deactivateActionMemo(config, values.amId, values.commentMessage);
            sendToast(
                <>
                    Deactivated Action Memo:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmProcessFormType.REACTIVATE) {
            await reactivateActionMemo(
                config,
                values.amId,
                new Date(values.completionDate),
                values.commentMessage,
                !RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
            );
            sendToast(
                <>
                    Reactivated Action Memo:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        } else if (formType === AmProcessFormType.CANCEL_REQUEST) {
            await cancelRequest(config, values.amId, values.commentMessage);
            sendToast(
                <>
                    Cancelled Status Change Request:{' '}
                    <strong>{formatAmDisplayString(values.amNumber, values.supNumber)}</strong>
                </>,
            );
        }

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`Process failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const AmProcessModal = (props: AmProcessModalProps) => {
    const { instance } = useMsal();
    const config = useContext(ConfigContext);
    const roles = RoleUtils.getRoles(instance);

    const [responseError, setResponseError] = useState('');

    const isCompletionDateNotRequired = () =>
        !(
            props.formType === AmProcessFormType.CHANGE_COMPLETION_DATE ||
            props.formType === AmProcessFormType.REOPEN ||
            props.formType === AmProcessFormType.REACTIVATE
        );

    const startDateMoment = moment();
    const maxCompletionDateMoment = startDateMoment.add(
        props.templateMaximumCompletionDays,
        'days',
    );

    const commentRequired =
        !RoleUtils.checkRole(roles, ['ActionMemo.Admin']) &&
        (props.formType === AmProcessFormType.CHANGE_COMPLETION_DATE ||
            props.formType === AmProcessFormType.REACTIVATE);

    const amProcessValidationSchema = Yup.object().shape({
        completionDate: Yup.date()
            .test(
                'required-check',
                'New Completion Date is required',
                (completionDate) =>
                    isCompletionDateNotRequired() ||
                    (!!completionDate &&
                        !moment(completionDate).isSame(moment(props.completionDate))),
            )
            .test(
                'after-check',
                'New Completion Date must be after today',
                (completionDate) =>
                    isCompletionDateNotRequired() || moment(completionDate).isAfter(moment()),
            )
            .test(
                'after-one-month-check',
                'Completion date must be 30 days after the creation date',
                (completionDate) =>
                    isCompletionDateNotRequired() ||
                    moment(completionDate).isAfter(
                        moment(props.creationDate).add(MINIMUM_COMPLETION_DAYS, 'days'),
                    ),
            )
            .test(
                'before-template-maximum',
                'Completion date must within the template maximum days',
                (completionDate) =>
                    isCompletionDateNotRequired() ||
                    RoleUtils.checkRole(roles, ['ActionMemo.Admin']) ||
                    moment(completionDate).isSameOrBefore(maxCompletionDateMoment),
            )
            .test(
                'before-decade-maximum',
                'Completion date must within 10 years',
                (completionDate) =>
                    isCompletionDateNotRequired() ||
                    moment(completionDate).isSameOrBefore(
                        moment(props.creationDate).add(MAXIMUM_COMPLETION_DAYS, 'days'),
                    ),
            ),
        commentMessage: Yup.string()
            // eslint-disable-next-line no-template-curly-in-string
            .max(COMMENT_MAX_LENGTH, 'Comment exceeds max length of ${max} characters')
            .test(
                'user-required',
                'Comment is required',
                (comment) => !commentRequired || !!comment,
            ),
    });

    let completionDateInitialValue = '';
    if (props.supNumber > 0 && props.newCompletionDate) {
        completionDateInitialValue = props.newCompletionDate;
    } else if (props.completionDate) {
        completionDateInitialValue = props.completionDate;
    }

    const initialValues = {
        amId: props.amId ? props.amId : INITIAL_SELECT_ID,
        amNumber: props.amNumber,
        supNumber: props.supNumber,
        completionDate: completionDateInitialValue,
        commentMessage: '',
    };

    let headerText = props.title;
    let actionText = 'Submit';
    switch (props.formType) {
        case AmProcessFormType.CANCEL:
            headerText = 'Cancel Action Memo';
            actionText = 'Cancel Action Memo';
            break;
        case AmProcessFormType.CANCEL_REQUEST:
            headerText = 'Cancel Status Change Request';
            actionText = 'Cancel Request';
            break;
        case AmProcessFormType.CHANGE_COMPLETION_DATE:
            headerText = 'Change Completion Date';
            break;
        case AmProcessFormType.MARK_CONFIDENTIAL:
            headerText = 'Mark Confidential';
            actionText = 'Mark Confidential';
            break;
        case AmProcessFormType.UNMARK_CONFIDENTIAL:
            headerText = 'Unmark Confidential';
            actionText = 'Unmark Confidential';
            break;
        case AmProcessFormType.CLOSE:
            headerText = 'Close Action Memo';
            actionText = 'Close Action Memo';
            break;
        case AmProcessFormType.REOPEN:
            headerText = 'Reopen Action Memo';
            actionText = 'Reopen';
            break;
        case AmProcessFormType.DEACTIVATE:
            headerText = 'Deactivate Action Memo';
            actionText = 'Deactivate';
            break;
        case AmProcessFormType.REACTIVATE:
            headerText = 'Reactivate Action Memo';
            actionText = 'Reactivate';
            break;
        default:
            break;
    }

    const isDateEnabled = (date: Date) => {
        const dateMoment = moment(date);
        const currentUserMaxCompleteDateMoment = !RoleUtils.checkRole(roles, ['ActionMemo.Admin'])
            ? maxCompletionDateMoment
            : moment(props.creationDate).add(MAXIMUM_COMPLETION_DAYS, 'days');
        return (
            dateMoment.isAfter(moment()) &&
            (dateMoment.isSameOrBefore(props.completionDate) ||
                dateMoment.isSameOrBefore(currentUserMaxCompleteDateMoment))
        );
    };

    const [enableSubmit, setEnableSubmit] = useState(
        (props.formType !== AmProcessFormType.CHANGE_COMPLETION_DATE &&
            props.formType !== AmProcessFormType.REACTIVATE) ||
            props.supNumber > 0 ||
            RoleUtils.checkRole(roles, ['ActionMemo.Admin']),
    );

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={amProcessValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitAmProcess(
                            config,
                            props.formType,
                            values,
                            roles,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={({ values, touched, errors, isSubmitting, dirty, resetForm }) => {
                        const checkboxChanged = (event: ChangeEvent<HTMLInputElement>) => {
                            setEnableSubmit(event.target.checked);
                            resetForm();
                        };

                        return (
                            <>
                                <Modal.Header closeButton>
                                    <Modal.Title>{headerText}</Modal.Title>
                                </Modal.Header>
                                <Form>
                                    <Modal.Body>
                                        <ResponseError errorMessage={responseError} />

                                        {props.formType === AmProcessFormType.MARK_CONFIDENTIAL && (
                                            <Alert variant="info">
                                                Please note by marking this action memo as
                                                confidential, access will be limited to the creator,
                                                project manager, and users specified on the
                                                approvers and viewers list. We recommend you review
                                                the viewer list after you mark the action memo
                                                confidential.
                                            </Alert>
                                        )}
                                        {props.formType ===
                                            AmProcessFormType.CHANGE_COMPLETION_DATE && (
                                            <>
                                                {props.supNumber <= 0 &&
                                                    !RoleUtils.checkRole(roles, [
                                                        'ActionMemo.Admin',
                                                    ]) && (
                                                        <>
                                                            <Alert variant="warning">
                                                                Extending the completion date cannot
                                                                be done if it will materially change
                                                                the scope of what was approved on
                                                                the action memo. A supplement should
                                                                be submitted if the scope, timeline,
                                                                or funding on the approved action
                                                                memo needs to change.
                                                            </Alert>

                                                            <div className="py-3">
                                                                <ModalConfirmCheckbox
                                                                    label="  By extending the completion date of this AM, I agree that the extension is within the nature and scope of the approvals granted by this AM and/or its supplements."
                                                                    onChangeCallback={
                                                                        checkboxChanged
                                                                    }
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                                {props.supNumber > 0 && (
                                                    <Alert variant="info">
                                                        Please note that this will update the new
                                                        completion date on the supplement which will
                                                        replace the original action memo completion
                                                        date when the supplement is approved.
                                                    </Alert>
                                                )}
                                            </>
                                        )}
                                        {props.formType === AmProcessFormType.REACTIVATE &&
                                            props.supNumber <= 0 &&
                                            !RoleUtils.checkRole(roles, ['ActionMemo.Admin']) && (
                                                <div className="py-3">
                                                    <ModalConfirmCheckbox
                                                        label="  By reactivating this AM and extending the completion date, I agree that the extension is within the nature and scope of the approvals granted by this AM and/or its supplements."
                                                        onChangeCallback={checkboxChanged}
                                                    />
                                                </div>
                                            )}
                                        {(((props.formType ===
                                            AmProcessFormType.CHANGE_COMPLETION_DATE ||
                                            props.formType === AmProcessFormType.REACTIVATE) &&
                                            enableSubmit) ||
                                            props.formType === AmProcessFormType.REOPEN) && (
                                            <DateField
                                                label="New Completion Date"
                                                name="completionDate"
                                                flatpickrProps={{
                                                    options: {
                                                        altInputClass: 'form-control',
                                                        altFormat: DATE_ONLY_FORMAT,
                                                        enableTime: false,
                                                        dateFormat: DATE_ONLY_FORMAT,
                                                        enable: [isDateEnabled],
                                                        static: true,
                                                    },
                                                    value: values.completionDate
                                                        ? moment(values.completionDate).format(
                                                              DATE_ONLY_FORMAT,
                                                          )
                                                        : undefined,
                                                }}
                                                timezone={SERVER_TIMEZONE}
                                                touched={touched.completionDate}
                                                errors={errors.completionDate}
                                                isRequired
                                                testId="completionDate"
                                                tooltip={`${
                                                    values.supNumber > 0
                                                        ? 'Replaces the completion date when supplement is approved.<br/>'
                                                        : ''
                                                }Maximum completion date allowed is ${maxCompletionDateMoment.format(
                                                    DATE_ONLY_FORMAT,
                                                )}`}
                                            />
                                        )}

                                        {enableSubmit && (
                                            <TextCountedField
                                                label={`Comment ${
                                                    !commentRequired ? '(Optional)' : ''
                                                }`}
                                                name="commentMessage"
                                                touched={touched.commentMessage}
                                                errors={errors.commentMessage}
                                                isRequired={commentRequired}
                                                testId="commentMessage"
                                                length={
                                                    values.commentMessage
                                                        ? values.commentMessage.length
                                                        : 0
                                                }
                                                maxLength={COMMENT_MAX_LENGTH}
                                            />
                                        )}
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={toggleModal}
                                            disabled={isSubmitting}
                                        >
                                            Close
                                        </Button>
                                        <SubmitButton
                                            className="btn-primary ml-auto"
                                            disabled={
                                                (props.formType ===
                                                    AmProcessFormType.CHANGE_COMPLETION_DATE ||
                                                    props.formType === AmProcessFormType.REOPEN ||
                                                    props.formType ===
                                                        AmProcessFormType.REACTIVATE) &&
                                                !dirty
                                            }
                                            isSubmitting={isSubmitting}
                                            buttonText={actionText}
                                        />
                                    </Modal.Footer>
                                </Form>
                            </>
                        );
                    }}
                />
            )}
        />
    );
};

export default AmProcessModal;
